'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSupport } from '@/context/SupportContext'
import { shouldHideSupportFab } from '@/lib/support/shouldHideSupportFab'
import { SupportPanel } from '@/app/components/support/SupportPanel'
import { SupportPanelLegacy } from '@/app/components/support/SupportPanel.legacy'
import { TF_SHOW_NEW_UI_LANDING_PAGE } from '@/lib/featureFlags'

const newFabClassName =
  'pointer-events-auto flex size-16 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full border-0 bg-white px-0 shadow-[0_4px_20px_rgba(97,85,245,0.35)] transition-all duration-200 hover:scale-105 hover:bg-[#efeefe] hover:shadow-[0_8px_32px_rgba(97,85,245,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 md:h-[64px] md:w-[182px] md:justify-start md:rounded-[20px] md:border-2 md:border-[#d946ef] md:px-3 md:shadow-[0_0_14px_rgba(217,70,239,0.65),0_6px_18px_rgba(97,85,245,0.25)] md:hover:scale-[1.02] md:hover:bg-white md:hover:shadow-[0_0_18px_rgba(217,70,239,0.8),0_8px_24px_rgba(97,85,245,0.3)] md:focus-visible:ring-[#d946ef]'

const newAvatarClassName =
  'size-full shrink-0 overflow-hidden rounded-full bg-white md:size-[40px] md:border-2 md:border-[#c94cf2]'

const newTextClassName =
  'hidden whitespace-nowrap text-[14px] font-semibold text-[#7d8296] md:inline'

const legacyFabClassName =
  'pointer-events-auto flex size-16 md:size-14 shrink-0 items-center justify-center rounded-full border-2 border-[#6155f5] bg-white overflow-hidden transition-all duration-200 hover:scale-105 hover:bg-[#efeefe] hover:shadow-[0_8px_32px_rgba(97,85,245,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 shadow-[0_4px_20px_rgba(97,85,245,0.35)]'

// Pages that render MainLayout's mobile bottom nav bar
const BOTTOM_NAV_PATHS = ['/dashboard', '/history', '/welcome']

function hasMobileBottomNav(pathname: string | null): boolean {
  if (!pathname) return false
  return BOTTOM_NAV_PATHS.some(
    p => pathname === p || pathname.startsWith(p + '/')
  )
}

export function SupportFabMinimal() {
  const pathname = usePathname()
  const { isOpen, openPanel, closePanel, isBlockedByOverlay } = useSupport()

  // sessionStorage isn't available during SSR, so this starts false to match
  // the server-rendered markup and is corrected right after mount/navigation.
  const [hasActiveInterviewId, setHasActiveInterviewId] = useState(false)
  useEffect(() => {
    setHasActiveInterviewId(!!sessionStorage.getItem('interviewId'))
  }, [pathname])

  const hidden = shouldHideSupportFab(pathname) || hasActiveInterviewId

  // If the panel was left open and the user navigates to a route where the
  // support widget should not appear (e.g. an interview summary page), close
  // it so a stale panel doesn't linger across navigation.
  useEffect(() => {
    if (hidden && isOpen) {
      closePanel()
    }
  }, [hidden, isOpen, closePanel])

  if (isBlockedByOverlay) {
    return null
  }

  if (hidden) {
    return null
  }

  // On pages with a mobile bottom nav, lift the FAB above it (~74px nav + 10px gap)
  const wrapperClassName = hasMobileBottomNav(pathname)
    ? 'pointer-events-none fixed z-[1000] max-md:right-[max(20px,env(safe-area-inset-right,0px))] max-md:bottom-[calc(76px+env(safe-area-inset-bottom,0px))] md:right-[max(2rem,env(safe-area-inset-right,0px))] md:bottom-[max(2rem,env(safe-area-inset-bottom,0px))]'
    : 'pointer-events-none fixed z-[1000] max-md:right-[max(20px,env(safe-area-inset-right,0px))] max-md:bottom-[max(20px,env(safe-area-inset-bottom,0px))] md:right-[max(2rem,env(safe-area-inset-right,0px))] md:bottom-[max(2rem,env(safe-area-inset-bottom,0px))]'

  if (!TF_SHOW_NEW_UI_LANDING_PAGE) {
    return (
      <>
        {!isOpen ? (
          <div className={wrapperClassName}>
            <button
              type="button"
              data-support-fab-trigger
              onClick={openPanel}
              className={legacyFabClassName}
              title="Support"
              aria-label="Open support chat"
            >
              <Image
                src="/images/support.png"
                alt=""
                width={56}
                height={56}
                className="size-full object-contain"
              />
            </button>
          </div>
        ) : null}
        <SupportPanelLegacy />
      </>
    )
  }

  return (
    <>
      {!isOpen ? (
        <div className={wrapperClassName}>
          <button
            type="button"
            data-support-fab-trigger
            onClick={openPanel}
            className={newFabClassName}
            title="Support"
            aria-label="Open support chat"
          >
            <div className={newAvatarClassName}>
              <Image
                src="/images/support.png"
                alt="Support"
                width={40}
                height={40}
                className="size-full object-cover"
              />
            </div>

            <span className={newTextClassName}>
              Chat with us...
            </span>
          </button>
        </div>
      ) : null}

      <SupportPanel />
    </>
  )
}
